/**
 * INSTALL A PLUGIN (PLUGINS.md §3 experts / §6) — one dialog, two choices when the plugin ships an expert:
 *   • the plugin AND its expert network (recommended — a working expert without guessing: its agents, rules,
 *     procedures and what it learned, wired to the plugin; you then create rooms and adapt it), or
 *   • the plugin only (its plumbing — for people building their own expert on top).
 * Installing = agreeing to what it asks for (listed). ``mode='create'`` = the plugin is already installed: only the
 * network part. ``onSubmit({createNetwork, expertId, name})`` resolves on success; a throw shows its message.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { describePermission } from './pluginPermissions.js';

const input = 'w-full rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-indigo-400 focus:outline-none';

export default function PluginInstallDialog({ open, plugin, mode = 'install', onSubmit, onCancel }) {
  const experts = plugin?.experts || [];
  const [withNetwork, setWithNetwork] = useState(true);
  const [expertId, setExpertId] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!open) return;
    const first = experts[0];
    setWithNetwork(mode === 'create' || experts.length > 0);
    setExpertId(first?.id || '');
    setName(first?.title || '');
    setBusy(false); setErr('');
  }, [open, plugin]); // eslint-disable-line react-hooks/exhaustive-deps
  const chosen = experts.find((x) => x.id === expertId);
  const perms = (plugin?.install?.needsConsent ? plugin.install.missing : plugin?.permissions) || [];
  const submit = async () => {
    if (withNetwork && !name.trim()) { setErr('Name the network.'); return; }
    setBusy(true); setErr('');
    try {
      await onSubmit?.({ createNetwork: withNetwork && !!chosen, expertId, name: name.trim() });
    } catch (e) {
      setErr(e?.data?.message || e?.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };
  const title = mode === 'create' ? `Create the ${chosen?.title || 'expert'} network`
    : `${plugin?.install?.needsConsent ? 'Allow the update of' : 'Install'} ${plugin?.displayName || ''}`;
  return (
    <AnimatePresence>
      {open && plugin && (
        <motion.div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="plugin-install-dialog"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel?.(); }}>
          <motion.div className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
            initial={{ scale: 0.98, y: 6 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.98, y: 6 }}>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4 text-xs">
              <h3 className="text-base font-semibold text-gray-900">{title}</h3>
              {plugin.description && mode !== 'create' ? <p className="text-gray-600">{plugin.description}</p> : null}
              {mode !== 'create' ? (
                <div data-testid="plugin-install-permissions">
                  <p className="font-medium text-gray-800">It asks to</p>
                  <ul className="mt-1 list-disc pl-4 text-gray-600">
                    {perms.length ? perms.map((p) => <li key={p}>{describePermission(p)}</li>) : <li>nothing beyond the platform</li>}
                  </ul>
                </div>
              ) : null}
              {experts.length && mode !== 'create' ? (
                <fieldset className="space-y-1.5" data-testid="plugin-install-choice">
                  <legend className="font-medium text-gray-800">What to install</legend>
                  <label className="flex items-start gap-2">
                    <input type="radio" name="plugin-install" checked={withNetwork} onChange={() => setWithNetwork(true)}
                      data-testid="plugin-install-with-network" />
                    <span><b>The plugin and its expert network</b> (recommended) — a working expert to start from: its
                      teams, rules, procedures and what it learned, already wired to the plugin. Create rooms and adapt it.</span>
                  </label>
                  <label className="flex items-start gap-2">
                    <input type="radio" name="plugin-install" checked={!withNetwork} onChange={() => setWithNetwork(false)}
                      data-testid="plugin-install-only" />
                    <span><b>The plugin only</b> — its connections and setups, for an expert you build yourself.</span>
                  </label>
                </fieldset>
              ) : null}
              {withNetwork && experts.length ? (
                <div className="space-y-1.5" data-testid="plugin-install-network">
                  {experts.length > 1 ? (
                    <select value={expertId} onChange={(e) => { setExpertId(e.target.value); setName((experts.find((x) => x.id === e.target.value) || {}).title || ''); }}
                      className={input} data-testid="plugin-install-expert">
                      {experts.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                    </select>
                  ) : null}
                  {chosen?.summary ? <p className="text-gray-600">{chosen.summary}</p> : null}
                  <label className="block">
                    <span className="font-medium text-gray-800">Network name</span>
                    <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} className={`${input} mt-1`}
                      data-testid="plugin-install-name" />
                  </label>
                  <p className="text-[11px] text-gray-500">It stays yours to change; when the plugin ships a better
                    version of this expert, you'll be offered the update — your own changes are kept.</p>
                </div>
              ) : null}
              {err ? <p className="text-[11px] text-rose-600" data-testid="plugin-install-error">{err}</p> : null}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-3">
              <button type="button" onClick={onCancel} disabled={busy}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button type="button" onClick={submit} disabled={busy} data-testid="plugin-install-submit"
                className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                {busy ? 'Working…' : mode === 'create' ? 'Create network'
                  : (withNetwork && experts.length ? 'Install + create network' : (plugin.install?.needsConsent ? 'Allow' : 'Install'))}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
